/**
 * @fileoverview Owner → Sub-Owners — invite additional logins on this owner
 * account and edit their per-screen None/Read/Write permissions (mirrors the
 * admin permission model). Sub-owners themselves cannot open this screen
 * (the API rejects them); the parent owner stays in full control.
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Stack, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import Input from '../../src/components/Input';
import ContentContainer from '../../src/components/ContentContainer';
import InlineConfirm from '../../src/components/InlineConfirm';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { titleCase } from '../../src/utils/format';

type Level = 'read' | 'write';
type Choice = 'none' | Level;

interface CatalogItem { label: string; levels: Level[]; readDesc?: string; writeDesc?: string }
interface Catalog { groups: { label: string; keys: string[] }[]; items: Record<string, CatalogItem> }
interface SubOwner {
  id: string; email: string; name: string; isActive: boolean;
  accepted: boolean; invitePending: boolean; inviteExpiresAt: string | null;
  createdAt: string; levels: Record<string, Level>;
}

export default function SubOwnersScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();

  const [list, setList] = useState<SubOwner[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftLevels, setDraftLevels] = useState<Record<string, Level>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const fetchAll = async (p = page, searchOverride?: string) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      const s = searchOverride ?? search;
      if (s) params.set('search', s);
      const [l, c] = await Promise.all([
        api<{ subOwners: SubOwner[]; total: number }>(`/api/owner/sub-owners?${params}`),
        catalog ? Promise.resolve(catalog) : api<Catalog>('/api/owner/sub-owners/catalog'),
      ]);
      setList(l.subOwners);
      setTotal(l.total);
      setPage(p);
      setCatalog(c);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };
  useFocusEffect(useCallback(() => { fetchAll(); }, []));

  const handleInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Name and email are required' });
      return;
    }
    setInviting(true);
    try {
      const res = await api<{ id: string }>('/api/owner/sub-owners', {
        method: 'POST',
        body: { name: inviteName.trim(), email: inviteEmail.trim().toLowerCase() },
      });
      toast.show({ type: 'success', title: 'Invited', message: `Invitation emailed to ${inviteEmail.trim()}` });
      setInviteOpen(false); setInviteName(''); setInviteEmail('');
      await fetchAll(1);
      // Jump straight into permission editing for the new sub-owner.
      setEditingId(res.id);
      setDraftLevels({});
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setInviting(false);
    }
  };

  const openEditor = (so: SubOwner) => {
    setEditingId(so.id);
    setDraftLevels({ ...so.levels });
  };

  const setChoice = (key: string, choice: Choice) => {
    setDraftLevels(prev => {
      const next = { ...prev };
      if (choice === 'none') delete next[key];
      else next[key] = choice;
      return next;
    });
  };

  const setGroupAll = (keys: string[], choice: Choice) => {
    setDraftLevels(prev => {
      const next = { ...prev };
      for (const k of keys) {
        if (choice === 'none') delete next[k];
        else if (catalog?.items[k]?.levels.includes(choice)) next[k] = choice;
        else if (choice === 'write' && catalog?.items[k]?.levels.includes('read')) next[k] = 'read';
      }
      return next;
    });
  };

  const savePermissions = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api(`/api/owner/sub-owners/${editingId}/permissions`, { method: 'PUT', body: { levels: draftLevels } });
      toast.show({ type: 'success', title: 'Saved', message: 'Permissions updated' });
      setEditingId(null);
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (so: SubOwner) => {
    try {
      await api(`/api/owner/sub-owners/${so.id}`, { method: 'PATCH', body: { isActive: !so.isActive } });
      toast.show({ type: 'success', title: so.isActive ? 'Deactivated' : 'Activated', message: titleCase(so.name) });
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const doDelete = async (id: string) => {
    try {
      await api(`/api/owner/sub-owners/${id}`, { method: 'DELETE' });
      setConfirmDeleteId(null);
      toast.show({ type: 'success', title: 'Removed' });
      fetchAll();
    } catch (err: any) {
      setConfirmDeleteId(null);
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const resendInvite = async (so: SubOwner) => {
    try {
      await api(`/api/owner/sub-owners/${so.id}/resend`, { method: 'POST', body: {} });
      toast.show({ type: 'success', title: 'Invite re-sent', message: so.email });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const renderChoice = (key: string, item: CatalogItem, current: Choice) => {
    const choices: Choice[] = ['none', ...(item.levels.includes('read') ? ['read' as const] : []), ...(item.levels.includes('write') ? ['write' as const] : [])];
    return (
      <View style={styles.segRow}>
        {choices.map(c => (
          <TouchableOpacity
            key={c}
            onPress={() => setChoice(key, c)}
            style={[styles.segBtn, current === c && { backgroundColor: colors.primary, borderColor: colors.primary }]}
          >
            <Text style={[styles.segText, current === c && { color: '#fff' }]}>
              {c === 'none' ? 'None' : c === 'read' ? 'Read' : 'Write'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  return (
    <>
      <Stack.Screen options={{
        title: 'Sub-Owners',
        headerLeft: () => (
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/owner/settings')} hitSlop={12} style={{ padding: spacing.sm, marginLeft: -spacing.xs }}>
            <Ionicons name="arrow-back" size={28} color={colors.primary} />
          </TouchableOpacity>
        ),
      }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={720}>
          <Text style={styles.title}>Sub-Owners</Text>
          <Text style={styles.subtitle}>
            Give partners or managers their own login on this account, with exactly the access you choose.
            Sub-owners can never manage other sub-owners.
          </Text>

          {!inviteOpen && (
            <Button
              title="Invite Sub-Owner"
              onPress={() => setInviteOpen(true)}
              icon={<Ionicons name="person-add" size={18} color={colors.white} />}
              size="sm"
              style={{ marginBottom: spacing.lg, alignSelf: 'flex-start' }}
            />
          )}

          {inviteOpen && (
            <Card style={{ marginBottom: spacing.lg }}>
              <Text style={styles.sectionTitle}>Invite Sub-Owner</Text>
              <Input label="Name" placeholder="e.g. Stacy" value={inviteName} onChangeText={setInviteName} autoCapitalize="words" />
              <Input label="Email" placeholder="them@example.com" value={inviteEmail} onChangeText={setInviteEmail} autoCapitalize="none" keyboardType="email-address" />
              <Text style={styles.hint}>They'll get an email to set a password. You can set permissions right after.</Text>
              <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm }}>
                <Button title="Cancel" variant="outline" onPress={() => setInviteOpen(false)} size="sm" />
                <Button title="Send Invite" onPress={handleInvite} loading={inviting} size="sm" />
              </View>
            </Card>
          )}

          <AdminToolbar
            search={search}
            onSearchChange={setSearch}
            onSearch={() => fetchAll(1)}
            total={total}
            page={page}
            limit={10}
            onPageChange={(p) => fetchAll(p)}
            csvEndpoint={`/api/owner/sub-owners${search ? `?search=${encodeURIComponent(search)}` : ''}`}
            csvFilename="sub-owners.csv"
            label="sub-owner"
          />

          {loading ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
          ) : list.length === 0 ? (
            <Card><Text style={styles.empty}>{search ? 'No sub-owners found.' : 'No sub-owners yet.'}</Text></Card>
          ) : (
            list.map(so => (
              <Card key={so.id} style={{ marginBottom: spacing.md }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <Text style={styles.name}>{titleCase(so.name)}</Text>
                      {!so.isActive && <Text style={[styles.badge, { color: colors.danger, backgroundColor: colors.danger + '15' }]}>Disabled</Text>}
                      {so.invitePending && <Text style={[styles.badge, { color: colors.warning, backgroundColor: colors.warning + '15' }]}>Invite pending</Text>}
                    </View>
                    <Text style={styles.email}>{so.email}</Text>
                    <Text style={styles.meta}>
                      {Object.keys(so.levels).length} permission{Object.keys(so.levels).length !== 1 ? 's' : ''} granted
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                    {so.invitePending && (
                      <TouchableOpacity onPress={() => resendInvite(so)} hitSlop={8} style={styles.iconBtn} accessibilityLabel="Resend invite">
                        <Ionicons name="mail-outline" size={20} color={colors.primary} />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={() => openEditor(so)} hitSlop={8} style={styles.iconBtn} accessibilityLabel="Edit permissions">
                      <Ionicons name="key-outline" size={20} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => toggleActive(so)} hitSlop={8} style={styles.iconBtn} accessibilityLabel={so.isActive ? 'Deactivate' : 'Activate'}>
                      <Ionicons name={so.isActive ? 'pause-circle-outline' : 'play-circle-outline'} size={20} color={colors.warning} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setConfirmDeleteId(so.id)} hitSlop={8} style={styles.iconBtn} accessibilityLabel="Delete">
                      <Ionicons name="trash-outline" size={20} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                </View>

                {confirmDeleteId === so.id && (
                  <InlineConfirm
                    message={`Remove ${titleCase(so.name)}'s login entirely?`}
                    visible={true}
                    onConfirm={() => doDelete(so.id)}
                    onCancel={() => setConfirmDeleteId(null)}
                    confirmLabel="Remove"
                    variant="danger"
                  />
                )}

                {editingId === so.id && catalog && (
                  <View style={{ marginTop: spacing.md, borderTopWidth: 1, borderTopColor: colors.borderLight, paddingTop: spacing.md }}>
                    {catalog.groups.map(g => (
                      <View key={g.label} style={{ marginBottom: spacing.md }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs }}>
                          <Text style={styles.groupTitle}>{g.label}</Text>
                          <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                            {(['none', 'read', 'write'] as Choice[]).map(c => (
                              <TouchableOpacity key={c} onPress={() => setGroupAll(g.keys, c)}>
                                <Text style={styles.bulkLink}>All {c}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>
                        {g.keys.map(k => {
                          const item = catalog.items[k];
                          if (!item) return null;
                          const current: Choice = draftLevels[k] ?? 'none';
                          return (
                            <View key={k} style={styles.permRow}>
                              <Text style={styles.permLabel} numberOfLines={2}>{item.label}</Text>
                              {renderChoice(k, item, current)}
                            </View>
                          );
                        })}
                      </View>
                    ))}
                    <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                      <Button title="Cancel" variant="outline" size="sm" onPress={() => setEditingId(null)} />
                      <Button title="Save Permissions" size="sm" onPress={savePermissions} loading={saving} />
                    </View>
                  </View>
                )}
              </Card>
            ))
          )}
          <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold as any, color: c.text, marginBottom: spacing.xs },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.lg, lineHeight: 20 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.sm },
  hint: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.xs },
  empty: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', paddingVertical: spacing.lg },
  name: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text },
  email: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  meta: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  badge: { fontSize: 10, fontWeight: '700', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, overflow: 'hidden' },
  iconBtn: { padding: spacing.xs },
  groupTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.bold as any, color: c.text, textTransform: 'uppercase', letterSpacing: 0.4 },
  bulkLink: { fontSize: fontSize.xs, color: c.primary, textDecorationLine: 'underline' },
  permRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6, borderTopWidth: 1, borderTopColor: c.borderLight, gap: spacing.sm },
  permLabel: { flex: 1, fontSize: fontSize.sm, color: c.text },
  segRow: { flexDirection: 'row', gap: 4 },
  segBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.borderLight },
  segText: { fontSize: fontSize.xs, color: c.text },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
